import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMyAddresses,
  saveAddress,
  deleteAddress,
  SavedAddress,
} from "../api";

type AddressDraft = {
  addressId?: string;
  label: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
  isDefault: boolean;
};

const EMPTY_DRAFT: AddressDraft = {
  label: "Home",
  street: "",
  city: "",
  state: "TX",
  zip: "",
  notes: "",
  isDefault: false,
};

const LABEL_PRESETS = ["Home", "Work", "Office", "Other"];

export default function SavedAddressesPage() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<AddressDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const list = await getMyAddresses();
      setAddresses(list);
    } catch (err: any) {
      console.error(err);
      setError("Couldn't load your addresses.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function startNew() {
    setEditing({ ...EMPTY_DRAFT, isDefault: addresses.length === 0 });
  }

  function startEdit(a: SavedAddress) {
    setEditing({
      addressId: a.addressId,
      label: a.label || "Home",
      street: a.street,
      city: a.city,
      state: a.state || "TX",
      zip: a.zip,
      notes: a.notes || "",
      isDefault: a.isDefault === true,
    });
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.street || !editing.city || !editing.zip) {
      setError("Street, city, and ZIP are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await saveAddress(editing);
      setEditing(null);
      await load();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || "Couldn't save the address.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this address?")) return;
    setDeletingId(id);
    try {
      await deleteAddress(id);
      await load();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || "Couldn't delete the address.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/profile")}
            className="text-sm text-mouna-blue font-bold whitespace-nowrap"
          >
            ← Profile
          </button>
          <h1 className="font-display text-lg font-bold text-navy truncate">
            Saved Addresses
          </h1>
          <button
            onClick={startNew}
            className="text-sm font-bold text-mouna-blue whitespace-nowrap"
          >
            + Add
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center text-gray-400 text-sm py-12">
            Loading…
          </div>
        )}

        {!loading && !error && addresses.length === 0 && !editing && (
          <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
            <div className="text-4xl mb-3">📍</div>
            <h3 className="font-display font-bold text-navy mb-1">
              No saved addresses yet
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Save your home, office, or anywhere you book services for faster
              checkout.
            </p>
            <button
              onClick={startNew}
              className="px-5 py-2.5 rounded-full bg-mouna-blue text-white text-sm font-bold hover:bg-blue-700 transition-colors"
            >
              + Add your first address
            </button>
          </div>
        )}

        {!loading && addresses.length > 0 && !editing && (
          <div className="space-y-3">
            {addresses.map((a) => (
              <div
                key={a.addressId}
                className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl">
                    {a.label.toLowerCase().includes("work") || a.label.toLowerCase().includes("office") ? "🏢" : "🏠"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-navy text-sm">
                        {a.label || "Saved address"}
                      </span>
                      {a.isDefault && (
                        <span className="text-xs bg-blue-100 text-mouna-blue px-2 py-0.5 rounded font-bold">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      {a.street}
                    </div>
                    <div className="text-sm text-gray-700">
                      {a.city}
                      {a.state && `, ${a.state}`} {a.zip}
                    </div>
                    {a.notes && (
                      <div className="text-xs text-gray-500 mt-1 italic">
                        {a.notes}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3 border-t border-gray-100 pt-3">
                  <button
                    onClick={() => startEdit(a)}
                    className="text-xs font-bold text-mouna-blue px-3 py-1.5 rounded-full hover:bg-blue-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(a.addressId)}
                    disabled={deletingId === a.addressId}
                    className="text-xs font-bold text-red-600 px-3 py-1.5 rounded-full hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === a.addressId ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form — shown when adding or editing */}
        {editing && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <h2 className="font-display font-bold text-navy mb-4">
              {editing.addressId ? "Edit address" : "Add a new address"}
            </h2>

            <Field label="Label">
              <div className="flex flex-wrap gap-2">
                {LABEL_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setEditing({ ...editing, label: p })}
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-bold border transition-colors " +
                      (editing.label === p
                        ? "bg-mouna-blue text-white border-mouna-blue"
                        : "bg-white text-navy border-gray-300 hover:border-mouna-blue")
                    }
                  >
                    {p}
                  </button>
                ))}
                <input
                  type="text"
                  value={editing.label}
                  onChange={(e) =>
                    setEditing({ ...editing, label: e.target.value })
                  }
                  placeholder="Custom label"
                  className="flex-1 min-w-[120px] px-3 py-1.5 rounded-full text-xs border border-gray-300 focus:border-mouna-blue focus:outline-none"
                />
              </div>
            </Field>

            <Field label="Street address *">
              <input
                type="text"
                value={editing.street}
                onChange={(e) =>
                  setEditing({ ...editing, street: e.target.value })
                }
                placeholder="123 Main St"
                className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="City *">
                <input
                  type="text"
                  value={editing.city}
                  onChange={(e) =>
                    setEditing({ ...editing, city: e.target.value })
                  }
                  placeholder="Houston"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="State">
                  <input
                    type="text"
                    maxLength={2}
                    value={editing.state}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        state: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="TX"
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm uppercase"
                  />
                </Field>
                <Field label="ZIP *">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    value={editing.zip}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        zip: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="77001"
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
                  />
                </Field>
              </div>
            </div>

            <Field label="Notes (optional)">
              <input
                type="text"
                value={editing.notes}
                onChange={(e) =>
                  setEditing({ ...editing, notes: e.target.value })
                }
                placeholder="Gate code 1234, leave at door, etc."
                className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
              />
            </Field>

            <label className="flex items-center gap-2 mt-2 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={editing.isDefault}
                onChange={(e) =>
                  setEditing({ ...editing, isDefault: e.target.checked })
                }
                className="w-4 h-4"
              />
              <span className="text-sm text-navy">
                Make this my default address
              </span>
            </label>

            <div className="flex gap-3">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 text-navy text-sm font-bold hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-full bg-mouna-blue text-white text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save address"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="text-xs font-bold text-gray-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
